import type HlsType from 'hls.js'
// @ts-expect-error TypeScript can find declaration file, but import still works
import mux from 'mux-embed'
import { VideoSourceInfo } from './iVideoManager'
import { IVideoManager } from './iVideoManager'
import { whenTrackingAllowed } from '../../components/consent/consentTrackingStore'

const MUX_ENV_KEY = process.env.NEXT_PUBLIC_REACT_APP_MUX_ENV_KEY

type HLSInfo = {
    constructor: any
    instance: HlsType
} | null

export class MP4VideoManager implements IVideoManager {
    hls: HLSInfo
    private hlsPromise: Promise<HLSInfo> | null = null
    // Mux Data writes its `muxData` viewer cookie and beacons to litix.io as
    // soon as it monitors an element, so monitoring waits for consent.
    private cancelPendingMuxMonitor: (() => void) | null = null
    // attachInstance is async (dynamic hls.js import), so duplicate calls for
    // the same source would run loadSource/attachMedia twice on one Hls
    // instance and wedge the MediaSource before any data is appended. Keyed by
    // manifest so attaching a different source (full-screen next/prev) still
    // goes through.
    private attachedManifestUrl: string | null = null

    constructor() {
        this.hls = null
    }

    getHls(): Promise<HLSInfo> {
        this.hlsPromise ??= import('hls.js')
            .then((hls) => {
                const Hls = hls.default
                this.hls = {
                    constructor: Hls,
                    instance: new Hls(),
                }
                return this.hls
            })
            .catch((error) => {
                console.error('Failed to load hls.js', error)
                this.hlsPromise = null
                return null
            })
        return this.hlsPromise
    }

    async attachInstance(videoObject: VideoSourceInfo) {
        const videoElement = videoObject.videoRef?.current
        if (!videoElement) return
        if (this.attachedManifestUrl === videoObject.manifestUrl) return
        this.attachedManifestUrl = videoObject.manifestUrl

        const hls = await this.getHls()
        if (hls === null) {
            this.attachedManifestUrl = null
            return
        }

        hls.instance.loadSource(videoObject.manifestUrl)
        if (hls.instance.media !== videoElement) {
            hls.instance.attachMedia(videoElement)
        }
        this.muxMonitor(videoObject)
    }
    muxMonitor(videoObject: VideoSourceInfo) {
        const videoElement = videoObject.videoRef?.current
        if (!videoElement) return

        const { playerVideo } = videoObject

        this.cancelPendingMuxMonitor?.()
        this.cancelPendingMuxMonitor = whenTrackingAllowed(() => {
            mux.monitor(videoElement, {
                debug: false,
                hlsjs: this.hls?.instance,
                Hls: this.hls?.constructor,
                data: {
                    env_key: MUX_ENV_KEY,

                    // Player Metadata
                    player_name: 'vlp',
                    player_init_time: Date.now(),

                    // Video Metadata
                    video_id: playerVideo.videoHash,
                    video_title: playerVideo.name,
                    video_duration: playerVideo.duration,
                    video_stream_type: 'on-demand',

                    // Custom Metadata
                    custom_1: playerVideo.clientName,
                    custom_2: 'mp4',
                },
            })
        })
    }
    detachInstance(videoObject: VideoSourceInfo) {
        this.cancelPendingMuxMonitor?.()
        this.cancelPendingMuxMonitor = null
        this.hls?.instance.destroy()
        this.hls = null
        this.hlsPromise = null
        this.attachedManifestUrl = null
        const videoElement = videoObject.videoRef?.current
        if (!videoElement) return
        videoElement.pause()
        videoElement.removeAttribute('src')
        videoElement.load()
    }
}
